import { CarouselLeftArrowIcon } from '@components/Icons/CarouselLeftArrowIcon';
import { CarouselRightArrowIcon } from '@components/Icons/CarouselRightArrowIcon';
import { useCommonState } from '@hooks/storeHooks/useCommonState';
import { useMusicState } from '@hooks/storeHooks/useMusicState';
import useAlbumData from '@hooks/useAlbumData';
import { useStationMode } from '@hooks/station';
import { useSwipeCarousel } from '@hooks/useSwipeCarousel';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FeatureItem } from 'src/types';
import { Features } from '../../../../config';
import { FeaturedSlide } from './FeaturedSlide';
import { FeaturedVideoCard } from './FeaturedVideoCard';
//import { FeaturedVideoCard } from './FeaturedVideoCard';

// Constants
const SCROLL_THRESHOLD = 25;
const SWIPE_THRESHOLD = 50;
// CSS Classes
const CAROUSEL_CONTAINER_CLASSES =
  'flex gap-[16px] md:gap-[16px] sm:gap-[8px] mb-10 overflow-x-auto snap-x snap-proximity py-[2px] overflow-y-hidden scrollbar-hide hide-scrollbar select-none scroll-pl-4 sm:scroll-pl-6 h-full';

export const FeaturedItems = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setActivePlaylist, featuredItems } = useMusicState();
  const { carouselItems, setActivePopup } = useCommonState();
  const { loadFeaturedItems, loadAlbumById } = useAlbumData();
  const isStationMode = useStationMode();
  const [current, setCurrent] = useState(0);
  const [playingSlideId, setPlayingSlideId] = useState<string | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const carouselRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (featuredItems.length === 0) {
      console.log('🎬 Loading featured items...');
      loadFeaturedItems()
        .then(() => {
          console.log('🎬 Featured items loaded successfully');
          setIsInitialLoading(false);
        })
        .catch((error) => {
          console.error('🎬 Featured items load failed:', error);
          setIsInitialLoading(false);
        });
    } else {
      setIsInitialLoading(false);
    }
  }, [featuredItems.length]);

  // Additional effect to handle when featuredItems actually get populated
  useEffect(() => {
    if (featuredItems.length > 0) {
      setIsInitialLoading(false);
    }
  }, [featuredItems]);
  // Add swipe functionality
  const { containerRef } = useSwipeCarousel({
    onSwipe: (direction) => {
      if (direction === 'right' && current < carouselItems.length - 1) {
        handleIndicatorClick(current + 1);
      } else if (direction === 'left' && current > 0) {
        handleIndicatorClick(current - 1);
      }
    },
    threshold: SWIPE_THRESHOLD,
  });
  const playNow = useCallback(
    async (slide: FeatureItem) => {
      if (slide.id) {
        try {
          if (slide.id === '1') {
            return;
          }

          // Check if this is a contributor (category is null and type is "Contributor")
          if (slide.category === null && slide.type === 'Contributor') {
            // Set loading state for this slide
            setPlayingSlideId(slide.id);

            // Small delay to show loading, then navigate
            setTimeout(() => {
              navigate(`/contributor/${slide.id}`);
              setPlayingSlideId(null);
            }, 500);
            return;
          }

          // Check if this is a creation (category is my-creations and type is "Creation")
          if (
            slide.category === 'my-creations' &&
            slide.type === 'Creation' &&
            slide.orgId
          ) {
            setPlayingSlideId(slide.id);
            setTimeout(() => {
              navigate(
                `/creations/track/${slide.orgId}/${slide.id}?ref=favorite`,
              );
              setPlayingSlideId(null);
            }, 500);
            return;
          }

          setPlayingSlideId(slide.id); // Set loading state
          const albumData = await loadAlbumById(slide.id);
          const album = { currentIndex: 0, album: albumData };
          setActivePlaylist(album);
          setActivePopup('musicplayer');
          // Navigation will be handled by App.tsx useEffect
        } catch (error) {
          console.error('Error loading album:', error);
        } finally {
          setPlayingSlideId(null); // Clear loading state
        }
      }
    },
    [
      setActivePlaylist,
      setActivePopup,
      loadAlbumById,
      setPlayingSlideId,
      navigate,
    ],
  );

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    const scrollLeft = container.scrollLeft;
    const maxScroll = container.scrollWidth - container.clientWidth;

    // Update arrow visibility
    setCanScrollLeft(scrollLeft > SCROLL_THRESHOLD);
    setCanScrollRight(scrollLeft < maxScroll - SCROLL_THRESHOLD);

    // If we're near the very end, force select the last slide
    if (scrollLeft >= maxScroll - SCROLL_THRESHOLD) {
      setCurrent(container.children.length - 1);
      return;
    }

    let closestIndex = 0;
    let minDistance = Infinity;

    Array.from(container.children).forEach((child, idx) => {
      const el = child as HTMLElement;
      const distance = Math.abs(el.offsetLeft - scrollLeft);
      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = idx;
      }
    });

    setCurrent(closestIndex);
  }, []);

  const handleIndicatorClick = useCallback(
    (index: number) => {
      const container = containerRef.current || carouselRef.current;
      if (container) {
        const slide = container.children[index] as HTMLElement;
        if (slide) {
          container.scrollTo({
            left: slide.offsetLeft,
            behavior: 'smooth',
          });
          setCurrent(index);
        }
      }
    },
    [containerRef],
  );

  // Show skeleton loader when initially loading and no featured items available
  // Note: carouselItems might be pre-loaded, so we only check featuredItems
  const shouldShowSkeleton = isInitialLoading && featuredItems.length === 0;

  if (shouldShowSkeleton) {
    return (
      <div className="relative w-full">
        <div className={CAROUSEL_CONTAINER_CLASSES}>
          {/* Initial spacer */}
          <div className="flex-shrink-0 w-4 sm:w-6"></div>

          {/* Skeleton slides - Music card style */}
          <div
            className={`flex-shrink-0 snap-start ${
              isStationMode ? 'w-[282px]' : 'w-[371px]'
            }`}>
            <div
              className={`relative w-full rounded-[16px] bg-gray-800 animate-pulse overflow-hidden shadow-[0_4px_16px_0_rgba(0,0,0,0.40)] ${
                isStationMode ? 'h-[395px]' : 'h-[520px]'
              }`}>
              {/* Background skeleton */}
              <div className="absolute top-0 left-0 w-full h-full bg-gray-700"></div>

              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0)_34.69%,rgba(0,0,0,0.9)_100%)] z-[99]" />

              {/* Badge skeleton */}
              <div className="absolute top-[16px] left-[16px] z-[100] w-[78px] h-[40px] bg-gray-600 rounded-full"></div>

              {/* Album cover with vinyl skeleton */}
              <div className="absolute bottom-[120px] left-[20px] z-[50] overflow-hidden w-[172px] h-[132px]">
                <div className="absolute top-0 left-[35px] w-[73px] h-[73px] md:w-[132px] md:h-[132px] rounded-full bg-gray-600 z-[5]"></div>
                <div className="absolute left-0 top-0 w-[73px] h-[73px] md:w-[132px] md:h-[132px] bg-gray-600 rounded-[8px] z-[10]"></div>
              </div>

              {/* Bottom Section skeleton */}
              <div className="absolute z-[100] bottom-0 left-0 p-5 flex flex-col gap-3 w-full">
                <div className="h-[28px] bg-gray-600 rounded w-4/5"></div>
              </div>
            </div>
          </div>

          {/* Skeleton slides - Serenity card style */}
          <div
            className={`flex-shrink-0 snap-start ${
              isStationMode ? 'w-[282px]' : 'w-[371px]'
            }`}>
            <div
              className={`relative w-full rounded-[16px] bg-gray-800 animate-pulse overflow-hidden shadow-[0_4px_16px_0_rgba(0,0,0,0.40)] ${
                isStationMode ? 'h-[395px]' : 'h-[520px]'
              }`}>
              <div className="absolute top-0 left-0 w-full h-full bg-gray-700"></div>
              <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0)_34.69%,rgba(0,0,0,0.9)_100%)] z-[99]" />
              <div className="absolute top-[16px] left-[16px] z-[100] w-[90px] h-[40px] bg-gray-600 rounded-full"></div>
              <div className="absolute z-[100] bottom-0 left-0 p-5 flex flex-col gap-3 w-full">
                <div className="h-[28px] bg-gray-600 rounded w-4/5"></div>
              </div>
            </div>
          </div>

          {/* Skeleton slides - Book card style */}
          <div
            className={`flex-shrink-0 snap-start ${
              isStationMode ? 'w-[282px]' : 'w-[371px]'
            }`}>
            <div
              className="relative w-full h-[520px] rounded-[16px] animate-pulse overflow-hidden shadow-[0_4px_16px_0_rgba(0,0,0,0.40)]"
              style={{
                background: 'linear-gradient(180deg, #3B4758 0%, #000000 100%)',
              }}>
              <div className="absolute top-[16px] left-[16px] z-[100] w-[110px] h-[40px] bg-gray-600/50 rounded-full"></div>
              <div className="absolute inset-0 flex top-[80px] justify-center z-[50]">
                <div className="w-[185px] h-[275.1px] rounded-[8px] bg-gray-600/50 shadow-lg"></div>
              </div>
              <div className="absolute z-[100] bottom-0 left-0 p-5 flex flex-col gap-3 w-full">
                <div className="h-[28px] bg-gray-600/50 rounded w-4/5"></div>
              </div>
            </div>
          </div>

          {/* Skeleton slides - Contributor card style */}
          <div
            className={`flex-shrink-0 snap-start ${
              isStationMode ? 'w-[282px]' : 'w-[371px]'
            }`}>
            <div
              className={`relative w-full rounded-[16px] bg-gray-800 animate-pulse overflow-hidden shadow-[0_4px_16px_0_rgba(0,0,0,0.40)] ${
                isStationMode ? 'h-[395px]' : 'h-[520px]'
              }`}>
              <div className="absolute top-0 left-0 w-full h-full bg-gray-700"></div>
              <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0)_34.69%,rgba(0,0,0,0.9)_100%)] z-[99]" />
              <div className="absolute top-[16px] left-[16px] z-[100] w-[90px] h-[40px] bg-gray-600 rounded-full"></div>
              <div className="absolute z-[100] bottom-0 left-0 p-5 flex flex-col gap-3 w-full">
                <div className="h-[28px] bg-gray-600 rounded w-4/5"></div>
              </div>
            </div>
          </div>

          {/* Last spacer */}
          <div className="flex-shrink-0 w-4 sm:w-[10%]"></div>
        </div>
      </div>
    );
  }

  // const featuredItemsFiltered = featuredItems.filter((item) => item.category !== null);
  return (
    <div className="relative w-full group">
      {/* Left Arrow - On Hover (Hidden at start) */}
      {canScrollLeft && (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const container = containerRef.current || carouselRef.current;
            if (container) {
              container.scrollBy({
                left: -400,
                behavior: 'smooth',
              });
            }
          }}
          className="absolute left-9 top-1/2 -translate-y-1/2 z-[300] hidden sm:flex items-center justify-center w-12 h-12 rounded-full bg-black/60 hover:bg-black/80 transition-all duration-200 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto"
          aria-label={t('aria.previousSlide')}>
          <CarouselLeftArrowIcon />
        </button>
      )}

      {/* Right Arrow - On Hover (Hidden at end) */}
      {canScrollRight && (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const container = containerRef.current || carouselRef.current;
            if (container) {
              container.scrollBy({
                left: 400,
                behavior: 'smooth',
              });
            }
          }}
          className="absolute right-9 top-1/2 -translate-y-1/2 z-[300] hidden sm:flex items-center justify-center w-12 h-12 rounded-full bg-black/60 hover:bg-black/80 transition-all duration-200 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto"
          aria-label={t('aria.nextSlide')}>
          <CarouselRightArrowIcon />
        </button>
      )}

      {/* Slides */}
      <div
        ref={(el) => {
          carouselRef.current = el;
          containerRef.current = el;
        }}
        id="main-carousel"
        className={CAROUSEL_CONTAINER_CLASSES}
        onScroll={handleScroll}>
        {/* Initial spacer */}
        <div className="flex-shrink-0 w-4 sm:w-6"></div>

        {/* Featured Video Card */}
        {/*<FeaturedVideoCard
          videoUrl="https://demoassets.touchcast.com/napster-music/featured/napster-featured-video.mp4"
          thumbnailUrl="https://demoassets.touchcast.com/napster-music/featured/napster-featured-video.jpg"
          title="Napster Featured"
          subtitle="Exclusive Content"
          description="Experience the best of Napster"
        />*/}

        {Features.enableFeaturedVideoCard && (
          <FeaturedVideoCard
            videoCardUrl="https://demoassets.touchcast.com/napster-music/featured/ind-ai-card.mp4"
            videoUrl="https://demoassets.touchcast.com/napster-music/featured/napster-ai-general.mp4"
            thumbnailUrl="https://demoassets.touchcast.com/napster-music/featured/ind-ai-min.jpeg"
            title="Napster Featured"
            subtitle="Exclusive Content"
            description="Experience the best of Napster"
          />
        )}

        {/* Items */}
        {carouselItems.map((slide: FeatureItem, index: number) => (
          <FeaturedSlide
            key={`carousel-${index}`}
            slide={slide}
            index={index}
            onPlayNow={playNow}
            isLoading={playingSlideId === slide.id}
            isCarouselItem={true}
          />
        ))}

        {featuredItems?.map((slide: FeatureItem, index: number) => (
          <FeaturedSlide
            key={`featured-${index}`}
            slide={slide}
            index={index}
            onPlayNow={playNow}
            isLoading={playingSlideId === slide.id}
            isCarouselItem={false}
          />
        ))}

        {/* Last spacer */}
        <div className="flex-shrink-0 w-4 sm:w-[10%]"></div>
      </div>

      {/* Indicators */}
      {/* Removed because its infinite scroll  */}
      {/* <CarouselIndicators
                itemCount={carouselItems.length}
                currentIndex={current}
                onIndicatorClick={handleIndicatorClick}
            /> */}
    </div>
  );
};
